import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./ChatMessages.css";
import { API_BASE_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";

const FEEDBACK_KEY = "chatFeedback";

const Message = memo(({ msg, index, feedback, handleFeedback, handleChatClick }) => {
  return (
    <div className={`message-container ${msg.role}`}>
      <div className="chat-container">
        <div className={`message ${msg.role}`}>
          <div className="message-text-container">
            {msg.role === "assistant" && (
              <div className="avatar-assistant">
                <img
                  src="./data/icon3.png"
                  alt="Assistant"
                  className="assistant-icon"
                />
              </div>
            )}
            <div className="message-text-text" onClick={handleChatClick}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {msg.text}
              </ReactMarkdown>
            </div>
          </div>
          {msg.role === "assistant" && msg.completed && (
            <div className="feedback-section">
              <div className="feedback-buttons">
                <button
                  onClick={() => handleFeedback(index, "like")}
                  className={`feedback-btn like-btn ${
                    feedback[index]?.type === "like" ? "selected" : ""
                  }`}
                  aria-label="Like"
                >
                  <i className="bi bi-hand-thumbs-up"></i>
                </button>
                <button
                  onClick={() => handleFeedback(index, "dislike")}
                  className={`feedback-btn dislike-btn ${
                    feedback[index]?.type === "dislike" ? "selected" : ""
                  }`}
                  aria-label="Dislike"
                >
                  <i className="bi bi-hand-thumbs-down"></i>
                </button>
              </div>
              {feedback[index]?.showThankYou && (
                <p className="feedback-thankyou">
                  Thank you for your feedback!
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const ThinkingIndicator = memo(() => {
  return (
    <div className="message-container assistant">
      <div className="chat-container">
        <div className="message assistant">
          <div className="message-text-container">
            <div className="avatar-assistant">
              <img
                src="./data/icon3.png"
                alt="Assistant"
                className="assistant-icon"
              />
            </div>
            <div className="message-text-text">
              <div className="spinner">
                <div className="loader"></div>
                <p>Thinking...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const ChatMessages = ({ chatHistory, isThinking, streaming, isMobile }) => {
  const { user, authFetch } = useAuth();
  const chatEndRef = useRef(null);
  const chatContentRef = useRef(null);
  const [feedback, setFeedback] = useState({});
  const initializedRef = useRef(false);

  // Load and persist feedback
  useEffect(() => {
    const storedFeedback = localStorage.getItem(FEEDBACK_KEY);
    if (storedFeedback) {
      try {
        setFeedback(JSON.parse(storedFeedback));
      } catch (error) {
        console.error("Error parsing stored feedback:", error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(feedback));
  }, [feedback]);

  // GUARANTEED SCROLL TO BOTTOM
  const scrollToBottom = useCallback(() => {
    if (!chatContentRef.current) return;
    
    // Method 1: Direct scroll (most reliable)
    chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    console.log(chatContentRef.current.scrollHeight)
    
    // Method 2: Double check after small delay
    setTimeout(() => {
      if (chatContentRef.current) {
        chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
      }
    }, 50);
  }, []);

  // INITIAL SCROLL WHEN COMPONENT MOUNTS
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
      initializedRef.current = true;
    }, 200); // Slightly longer delay for initial render
    
    return () => clearTimeout(timer);
  }, [scrollToBottom]);

  // SCROLL ON NEW MESSAGES
  useEffect(() => {
    if (initializedRef.current) {
      scrollToBottom();
    }
  }, [chatHistory.length, scrollToBottom]);

  // // CONTINUOUS SCROLL DURING STREAMING
  // useEffect(() => {
  //   if (streaming || isThinking) {
  //     const interval = setInterval(scrollToBottom, 0);
  //     return () => clearInterval(interval);
  //   }
  // }, [streaming, isThinking, scrollToBottom]);

  useEffect(() => {
    const lastMessage = chatHistory[chatHistory.length - 1];
    // If the last message is from the assistant and still streaming
    if (lastMessage && lastMessage.role === "assistant" && !lastMessage.completed) {
      scrollToBottom();
    }
  }, [chatHistory[chatHistory.length - 1]?.text, scrollToBottom]);
  

  const handleChatClick = useCallback((e) => {
    const anchor = e.target.closest("a");
    if (anchor && anchor.href && anchor.href.includes("realtyseek.ai/downloads/brochures")) {
      e.preventDefault();
      const tempAnchor = document.createElement("a");
      tempAnchor.href = anchor.href;
      tempAnchor.setAttribute("download", "");
      tempAnchor.setAttribute("target", "_blank");
      tempAnchor.setAttribute("rel", "noopener noreferrer");
      document.body.appendChild(tempAnchor);
      tempAnchor.click();
      document.body.removeChild(tempAnchor);
    }
  }, []);

  const handleFeedback = useCallback((index, type) => {
    if (feedback[index]?.type === type) return;

    setFeedback((prev) => ({
      ...prev,
      [index]: { type, showThankYou: true },
    }));

    setTimeout(() => {
      setFeedback((prev) => {
        const updated = { ...prev };
        if (updated[index]) {
          updated[index].showThankYou = false;
        }
        return updated;
      });
    }, 3000);

    const assistantMessage = chatHistory[index]?.text || "";
    let userMessage = "";
    if (index > 0 && chatHistory[index - 1]?.role === "user") {
      userMessage = chatHistory[index - 1].text;
    }

    let user_id, name, mobile;
    if (user && user.email) {
      user_id = user.email;
      name = user.name || "";
      mobile = user.mobile || localStorage.getItem("userMobile") || "";
    } else {
      user_id = localStorage.getItem("guestId") || "guest";
      name = localStorage.getItem("guestName") || "";
      mobile = localStorage.getItem("guestMobile") || "";
    }

    const payload = {
      user_id,
      name,
      mobile,
      feedback_type: type,
      rating: type === "like" ? 5.0 : 1.0,
      user_input: userMessage,
      response: assistantMessage,
      comment: "",
    };

    authFetch(`${API_BASE_URL}/api/feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }).catch((error) => {
      console.error("Error submitting feedback", error);
    });
  }, [feedback, chatHistory, user, authFetch]);

  return (
    <div 
      id="chat-content" 
      className="chat-content"
      ref={chatContentRef}
    >
      {(chatHistory || []).map((msg, index) => (
        <Message
          key={`msg-${index}-${msg.text.length}`}
          msg={msg}
          index={index}
          feedback={feedback}
          handleFeedback={handleFeedback}
          handleChatClick={handleChatClick}
        />
      ))}
      {isThinking && <ThinkingIndicator />}
      <div 
        ref={chatEndRef} 
        style={{ 
          float: "left", 
          clear: "both",
          height: "1px",
          width: "100%"
        }}
      />
    </div>
  );
};

export default memo(ChatMessages);